/**
 * Parler SPA - Main Entry Point
 * 
 * Integrates:
 * - Router with History API
 * - Page modules
 */

import { router } from './router.js?v=20260716b-ROUTERFIX';

import { Navbar } from './shared/components/Navbar.js?v=20260716b-ROUTERFIX';
import { APP_ROUTES, apiPath } from './shared/config/paths.js';

const ROUTE_VISIBILITY_KEYS = new Map([
   [APP_ROUTES.root, 'parler'],
   [APP_ROUTES.dashboard, 'parler'],
  [APP_ROUTES.summary, 'summary'],
  [APP_ROUTES.whatsapp, 'whatsapp'],
  [APP_ROUTES.templates, 'templates'],
  [APP_ROUTES.knowledgeBase, 'knowledge_base'],
  [APP_ROUTES.invoice, 'invoice'],
  [APP_ROUTES.crm, 'crm'],
  [APP_ROUTES.scheduler, 'scheduler'],
  [APP_ROUTES.clients, 'clients'],
  [APP_ROUTES.overview, 'overview'],
]);

const ACCESSIBLE_ROUTE_PRIORITY = [
  APP_ROUTES.root,
  APP_ROUTES.summary,
  APP_ROUTES.whatsapp,
  APP_ROUTES.templates,
  APP_ROUTES.knowledgeBase,
  APP_ROUTES.invoice,
  APP_ROUTES.crm,
  APP_ROUTES.scheduler,
  APP_ROUTES.clients,
  APP_ROUTES.overview,
];

// ============================================================================
// Route Registration
// ============================================================================

const pageLoaders = Object.freeze({
  clientManagement: () => import('./pages/ClientManagement.js?v=20260219a-NOSEO'),
  dashboard: () => import('./pages/Dashboard.js?v=20260321h-NOSEO'),
  overview: () => import('./pages/Overview.js?v=20260219a-NOSEO'),
  summary: () => import('./pages/Summary.js?v=20260321h-NOSEO'),
  whatsapp: () => import('./pages/WhatsApp/index.js?v=20260804d-WA-CHAT-IMPROVEMENTS'),
  templates: () => import('./pages/Templates/index.js?v=20260804c-TEMPLATES-DEEPLINK'),
  crm: () => import('./pages/CRM.js?v=20260321h-NOSEO'),
  invoice: () => import('./pages/Invoice.js?v=20260321h-NOSEO'),
  scheduler: () => import('./pages/Scheduler.js?v=20260321h-NOSEO'),
  login: () => import('./pages/Login.js?v=20260321h-NOSEO'),
  admin: () => import('./pages/Admin.js?v=20260321h-NOSEO'),
  knowledgeBase: () => import('./pages/KnowledgeBase/index.js?v=20260804e-KB-RENAME'),
  register: () => import('./pages/Register.js?v=20260603a-MERGED'),
  profile: () => import('./pages/Profile.js?v=20260603a-MERGED'),
});

[
  [APP_ROUTES.root, pageLoaders.dashboard],
  [APP_ROUTES.dashboard, pageLoaders.dashboard],
  [APP_ROUTES.summary, pageLoaders.summary],
  [APP_ROUTES.whatsapp, pageLoaders.whatsapp],
  [APP_ROUTES.invoice, pageLoaders.invoice],
  [APP_ROUTES.templates, pageLoaders.templates],
  [APP_ROUTES.crm, pageLoaders.crm],
  [APP_ROUTES.scheduler, pageLoaders.scheduler],
  [APP_ROUTES.clients, pageLoaders.clientManagement],
  [APP_ROUTES.overview, pageLoaders.overview],
  [APP_ROUTES.login, pageLoaders.login],
  [APP_ROUTES.admin, pageLoaders.admin],
  [APP_ROUTES.knowledgeBase, pageLoaders.knowledgeBase],
  [APP_ROUTES.register, pageLoaders.register],
  [APP_ROUTES.profile, pageLoaders.profile],
].forEach(([path, loader]) => {
  router.register(path, loader);
});

// ============================================================================
// App Initialization
// ============================================================================

class ParlerApp {
  constructor() {
    this.initialized = false;
    this._navbar = null;
    this._visibilityCache = null;
    this._visibilityCacheUserId = null;
    this._visibilityLoadPromise = null;
    this._handleVisibilityUpdated = this._handleVisibilityUpdated.bind(this);
  }

  _getAuthToken() {
    try {
      return window.localStorage.getItem('auth_token');
    } catch (_) {
      return null;
    }
  }

  _decodeJwtPayload(token) {
    try {
      if (!token || typeof token !== 'string') return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
      const json = atob(b64 + pad);
      const payload = JSON.parse(json);
      return payload && typeof payload === 'object' ? payload : null;
    } catch (_) {
      return null;
    }
  }

  _isTokenValid(token) {
    try {
      if (!token || typeof token !== 'string') return false;
      const payload = this._decodeJwtPayload(token);
      if (!payload || typeof payload !== 'object') return false;

      const exp = Number(payload.exp || 0);
      if (Number.isFinite(exp) && exp > 0 && Date.now() >= exp * 1000) {
        try { window.localStorage.removeItem('auth_token'); } catch (_) {}
        return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  _installAuthGuard() {
    const publicRoutes = new Set([
      APP_ROUTES.login,
      APP_ROUTES.register,
      APP_ROUTES.admin,
      `${APP_ROUTES.admin}/`,
    ]);
    router.setBeforeNavigate(async ({ path }) => {
      const { isAuthed } = this._getCurrentUserContext();

      if (!isAuthed && !publicRoutes.has(path)) {
        return { redirectTo: APP_ROUTES.login };
      }

      if (isAuthed && path === APP_ROUTES.login) {
        return { redirectTo: APP_ROUTES.root };
      }

      const visibilityKey = ROUTE_VISIBILITY_KEYS.get(path);
      if (!visibilityKey) return null;

      let visibility = null;
      try {
        visibility = await this._getVisibility();
      } catch (_) {
        visibility = null;
      }

      if (!visibility || visibility[visibilityKey] !== false) {
        return null;
      }

      const fallbackPath = this._getFirstAccessibleRoute(visibility);
      if (fallbackPath && fallbackPath !== path) {
        return { redirectTo: fallbackPath };
      }

      this._renderAccessDenied();
      return false;

      return null;
    });
  }

  _getCurrentUserContext() {
    const token = this._getAuthToken();
    const isAuthed = this._isTokenValid(token);
    const payload = isAuthed ? this._decodeJwtPayload(token) : null;
    const userId = payload && payload.user_id ? String(payload.user_id) : null;
    return { token, isAuthed, payload, userId };
  }

  _resetVisibilityCache() {
    this._visibilityCache = null;
    this._visibilityCacheUserId = null;
    this._visibilityLoadPromise = null;
  }

  _getFirstAccessibleRoute(visibility) {
    for (const path of ACCESSIBLE_ROUTE_PRIORITY) {
      const key = ROUTE_VISIBILITY_KEYS.get(path);
      if (!key || visibility[key] !== false) {
        return path;
      }
    }
    return null;
  }

  _renderAccessDenied() {
    const mount = document.getElementById('main-content');
    if (!mount) return;
    mount.innerHTML = `
      <div class="error-splash">
        <h1>Access Restricted</h1>
        <p>This app is hidden for your account.</p>
      </div>
    `;
  }

  _handleVisibilityUpdated(event) {
    const detail = event && event.detail ? event.detail : {};
    const currentUserId = this._getCurrentUserContext().userId;
    if (detail.userId && currentUserId && detail.userId !== currentUserId) {
      return;
    }

    this._resetVisibilityCache();
    if (!this.initialized) return;

    this.renderNavbar().catch(() => {});
    router.navigate(window.location.pathname, false);
  }

  _logout() {
    try {
      window.localStorage.removeItem('auth_token');
      window.localStorage.removeItem('parler_token');
      window.localStorage.removeItem('parler:whatsapp:messagesApiToken');
      window.sessionStorage.removeItem('auth_token');
      window.sessionStorage.removeItem('parler_token');
    } catch (_) {
      // ignore
    }
    this._resetVisibilityCache();
    window.location.href = APP_ROUTES.login;
  }

  async _fetchVisibility() {
    const { token, isAuthed, userId } = this._getCurrentUserContext();
    if (!isAuthed) return null;
    const visibilityApi = apiPath('/admin/visibility');
    const url = userId
      ? `${visibilityApi}?user_id=${encodeURIComponent(userId)}`
      : visibilityApi;

    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let res = await fetch(url, { headers, cache: 'no-store' });
    // If auth token was rejected, retry without it (visibility is a public endpoint)
    if (res.status === 401 && token) {
      res = await fetch(visibilityApi, { cache: 'no-store' });
    }
    if (!res.ok) return null;
    const j = await res.json();
    return (j && j.data) || null;
  }

  async _getVisibility(force = false) {
    const { userId } = this._getCurrentUserContext();
    if (force || this._visibilityCacheUserId !== userId) {
      this._resetVisibilityCache();
      this._visibilityCacheUserId = userId;
    }

    if (!force && this._visibilityCache) {
      return this._visibilityCache;
    }
    if (!force && this._visibilityLoadPromise) {
      return this._visibilityLoadPromise;
    }

    this._visibilityLoadPromise = this._fetchVisibility()
      .then((visibility) => {
        this._visibilityCache = visibility;
        this._visibilityCacheUserId = userId;
        return visibility;
      })
      .finally(() => {
        this._visibilityLoadPromise = null;
      });

    return this._visibilityLoadPromise;
  }
  
  async init() {
    try {
      console.log('[Parler] Initializing router...');

      window.addEventListener('parler:visibility-updated', this._handleVisibilityUpdated);
      this._installAuthGuard();
      await this.renderNavbar();
      // Initialize router
      router.init('#main-content');
      
      this.initialized = true;
      console.log('[Parler] ✅ Application initialized successfully');
    } catch (error) {
      console.error('[Parler] ❌ Initialization failed:', error);
      console.error('[Parler] Error stack:', error.stack);
      this.showErrorState(error);
      throw error;
    }
  }
  
  showErrorState(error) {
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div class="error-splash">
          <h1>Application Error</h1>
          <p>Failed to initialize Parler. Please refresh the page.</p>
          <p class="error-details">${error.message}</p>
          <button onclick="location.reload()">Reload Page</button>
        </div>
      `;
    }
  }

  async renderNavbar() {
    const navbarMount = document.getElementById('navbar');
    if (!navbarMount) return;

    if (this._navbar && typeof this._navbar.unmount === 'function') {
      this._navbar.unmount();
    }

    // base links (primary nav)
    const baseLinks = [
      { key: 'parler', label: 'Parler', href: APP_ROUTES.root },
      { key: 'summary', label: 'Summary', href: APP_ROUTES.summary },
      { key: 'whatsapp', label: 'WhatsApp', href: APP_ROUTES.whatsapp },
      { key: 'templates', label: 'Message Templates', href: APP_ROUTES.templates },
      { key: 'knowledge_base', label: 'Knowledge Base', href: APP_ROUTES.knowledgeBase },
      { key: 'invoice', label: 'Invoice', href: APP_ROUTES.invoice },
      { key: 'crm', label: 'Data Collection Tool', href: APP_ROUTES.crm },
      { key: 'scheduler', label: 'Scheduler', href: APP_ROUTES.scheduler },
    ];

    const { token, isAuthed, payload } = this._getCurrentUserContext();
    const username = payload && payload.username ? String(payload.username) : '';
    const user = isAuthed
      ? { username: username || 'Account' }
      : null;

    // fetch per-user visibility for nav filtering
    let visibility = null;
    if (isAuthed) {
      try {
        visibility = await this._getVisibility();
      } catch (err) {
        visibility = null;
      }
    }

    const filteredLinks = (isAuthed ? baseLinks : []).filter((lnk) => {
      if (!visibility) return true; // no config => show all
      const key = String(lnk.key || lnk.label || '').toLowerCase();
      // default to visible unless explicitly false
      return visibility[key] !== false;
    });

    this._navbar = Navbar({
      title: 'PARLER',
      links: filteredLinks,
      user,
      onLogout: () => this._logout(),
    });

    navbarMount.innerHTML = this._navbar.render();
    if (typeof this._navbar.mount === 'function') {
      this._navbar.mount();
    }
  }
}

// ============================================================================
// Start Application
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  const app = new ParlerApp();
  app.init();
});
